import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useDistanceCalculation } from '@/composables/useDistanceCalculation'
import { validateReferralCode } from '@/utils/referralCode'

export const usePriceStore = defineStore('servicePriceStore', () => {
  const serviceType = ref('doctor') // Service type (using service key)
  const onlineServiceForm = ref(false)
  const distance = ref(0) // Distance in kilometers
  const otherContent = ref('')
  const userLocation = ref(null) // User's selected location
  const totalTimeEstimate = ref(45) // Time estimate in minutes
  const isReferralApplied = ref(false) // Referral discount state
  const referralCode = ref('') // Current referral code

  // Initialize distance calculation composable
  const { getDistanceToCompany } = useDistanceCalculation()

  // Service type base prices
  const SERVICE_PRICES = {
    'doctor': 99,        // Doctor visit
    'government': 99,   // Government office
    'school': 99,        // School meeting
    'bank': 99,          // Bank matters
    'other': 99          // Other matters
  }

  const serviceTypesNameMappings = {
    'doctor': 'Wizyta medyczna',
    'government': 'Sprawa w urzędzie',
    'school': 'Spotkanie w szkole',
    'bank': 'Sprawa w banku',
    'other': 'Inna sprawa',
  }

  const PRICE_PER_KM = 0.23 // Price per kilometer in euros

  // Computed total price
  const totalPrice = computed(() => {
    const serviceKey = serviceType.value || 'other'
    const basePrice = SERVICE_PRICES[serviceKey] || SERVICE_PRICES['other']
    const timeMultiplier = totalTimeEstimate.value / 45
    const servicePrice = basePrice * timeMultiplier
    const distancePrice = distance.value * PRICE_PER_KM
    let total = servicePrice + distancePrice
    
    // Apply 10% discount if referral code is valid
    if (isReferralApplied.value) {
      total = total * 0.95
    }
    
    return total.toFixed(2)
  })

  // Getters and setters
  function setServiceType(type) {
    serviceType.value = type
  }

  function setDistance(km) {
    distance.value = km
  }

  function setUserLocation(location) {
    userLocation.value = location
    // Automatically update distance when user location changes
    if (location) {
      const calculatedDistance = getDistanceToCompany(location)
      setDistance(calculatedDistance)
    } else {
      setDistance(0)
    }
  }

  function getServiceTypes() {
    return Object.keys(SERVICE_PRICES).map(type => ({
      value: type,
      label: serviceTypesNameMappings[type]
    }))
  }

  function getServiceFormTypes() {
    return [{ label: 'Tłumaczenie z dojazdem', value: false} , { label: 'Tłumaczenie online', value: true }]
  }

  function setServiceForm(isOnline) {
    onlineServiceForm.value = isOnline
  }

  function setOtherContent(content) {
    otherContent.value = content
  }

  function setTotalTimeEstimate(minutes) {
    totalTimeEstimate.value = minutes
  }

  // Helper function to get display name from service key
  function getServiceTypeDisplayName(key = serviceType.value) {
    return serviceTypesNameMappings[key] || serviceTypesNameMappings['other']
  }

  // Helper function to get service key from display name
  function getServiceKeyFromDisplayName(displayName) {
    const entry = Object.entries(serviceTypesNameMappings).find(([, value]) => value === displayName)
    return entry ? entry[0] : 'other'
  }

  // Referral code validation function
  async function validateReferralCodeAndApply(code) {
    try {
      const result = await validateReferralCode(code)
      if (result.valid) {
        isReferralApplied.value = true
        referralCode.value = code
        return { success: true, message: 'Kod polecający został zastosowany!' }
      } else {
        isReferralApplied.value = false
        referralCode.value = ''
        return { success: false, message: 'Nieprawidłowy kod polecający' }
      }
    } catch (error) {
      console.error('Error validating referral code:', error)
      isReferralApplied.value = false
      referralCode.value = ''
      return { success: false, message: 'Błąd podczas sprawdzania kodu' }
    }
  }

  // Reset referral discount
  function resetReferralDiscount() {
    isReferralApplied.value = false
    referralCode.value = ''
  }

  return {
    serviceType,
    distance,
    totalPrice,
    userLocation,
    totalTimeEstimate,
    isReferralApplied,
    referralCode,
    setServiceType,
    setDistance,
    setUserLocation,
    getServiceTypes,
    otherContent,
    setOtherContent,
    setTotalTimeEstimate,
    getServiceTypeDisplayName,
    getServiceKeyFromDisplayName,
    getServiceFormTypes,
    setServiceForm,
    onlineServiceForm,
    validateReferralCodeAndApply,
    resetReferralDiscount
  }

})